import { useCallback, useRef, useState, type DragEvent } from "react"

/**
 * Drag-and-drop file handling for a drop zone. Returns `isDragging` (for the
 * visual affordance) and the handlers to spread onto the target element. Uses
 * a counter so nested children don't flicker the drag state on dragenter/leave.
 */
export function useFileDrop(onFiles: (files: File[]) => void, disabled?: boolean) {
  const [isDragging, setIsDragging] = useState(false)
  const depth = useRef(0)

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (disabled) return
      if (!Array.from(e.dataTransfer.types).includes("Files")) return
      e.preventDefault()
      depth.current += 1
      setIsDragging(true)
    },
    [disabled],
  )

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (disabled) return
      if (Array.from(e.dataTransfer.types).includes("Files")) e.preventDefault()
    },
    [disabled],
  )

  const onDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setIsDragging(false)
  }, [])

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (disabled) return
      e.preventDefault()
      depth.current = 0
      setIsDragging(false)
      const files = Array.from(e.dataTransfer.files)
      if (files.length) onFiles(files)
    },
    [disabled, onFiles],
  )

  return { isDragging, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } }
}
